// Detailed, rule-based trading analysis for the Insights page. Every number comes
// straight from the trader's trades and journal; nothing is estimated or generated.
import { formatMoney, netPnl, tradingDayKey, usd } from './pnl';
import { tagKind } from './tags';
import { rootSymbol } from './tools/contracts';
import { translateNow as tn } from './langState';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// "Mondays are your weakest day" and "trade smaller on Mondays".
const WEEKDAYS_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const ON_WEEKDAYS = ['on Sundays', 'on Mondays', 'on Tuesdays', 'on Wednesdays', 'on Thursdays', 'on Fridays', 'on Saturdays'];
const MIN_TRADES = 3; // smallest group worth reporting on
// Reset taps saved before entries carried the button's id, matched by their text.
export const RESET_TEXT = /^(Hit the reset button|Usé el botón de reinicio)/;
const MIN_DAYS = 2;
// Below these, a finding is shown as an early observation rather than a pattern.
const SOLID_TRADES = 15;
const SOLID_DAYS = 5;


function stats(trades) {
  const nets = trades.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const net = nets.reduce((a, b) => a + b, 0);
  return {
    count: trades.length,
    net,
    avg: trades.length ? net / trades.length : 0,
    winRate: trades.length ? (wins.length / trades.length) * 100 : 0,
    grossLoss: -losses.reduce((a, b) => a + b, 0),
  };
}

function group(trades, keyFn) {
  const map = new Map();
  for (const t of trades) {
    const key = keyFn(t);
    if (key === null || key === undefined) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  }
  return map;
}

function timeLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = tn(h < 12 ? 'AM' : 'PM');
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

export function holdMinutes(t) {
  if (!t.exitDate) return null;
  const m = (new Date(t.exitDate) - new Date(t.date)) / 60000;
  return m >= 0 ? m : null;
}

export const HOLD_BUCKETS = [
  { label: 'Under 2 min', max: 2 },
  { label: '2–10 min', max: 10 },
  { label: '10–30 min', max: 30 },
  { label: '30–60 min', max: 60 },
  { label: '1 hour +', max: Infinity },
];

const pct = (n) => `${Math.round(n)}%`;
const tradesText = (n) => tn(n === 1 ? '{n} trade' : '{n} trades', { n });
const daysText = (n) => tn(n === 1 ? '{n} day' : '{n} days', { n });

// Markets grouped into families, so 1 NQ and 3 MNQ count as the same market.
const FAMILY = {
  MNQ: 'nasdaq', NQ: 'nasdaq', NNQ: 'nasdaq', MES: 'sp', ES: 'sp', NES: 'sp', MYM: 'dow', YM: 'dow', NDOW: 'dow',
  M2K: 'russell', RTY: 'russell', N2K: 'russell', MGC: 'gold', GC: 'gold', MCL: 'crude', CL: 'crude', QM: 'crude',
  SIL: 'silver', SI: 'silver', M6E: 'euro', '6E': 'euro', MBT: 'bitcoin', BTC: 'bitcoin', MET: 'ether', ETH: 'ether',
};
const FAMILY_NAME = { nasdaq: 'Nasdaq', sp: 'S&P 500', dow: 'Dow', russell: 'Russell 2000', gold: 'Gold', crude: 'Crude oil',
  silver: 'Silver', euro: 'Euro', bitcoin: 'Bitcoin', ether: 'Ether' };

// The market a trade belongs to: a futures family, an option's underlying, or the symbol.
function marketOf(t) {
  const option = String(t.symbol || '').toUpperCase().match(/^([A-Z]{1,6})\d{6}[CP]\d{8}$/);
  if (option) return `${option[1]} options`;
  const root = rootSymbol(t.symbol);
  return FAMILY[root] || root;
}

function listWords(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${tn('and')} ${items[items.length - 1]}`;
}

// Turns grouped trades into rows for a chart: { key, label, trades, ...stats }, keeping order.
function rows(map, labelFn, order) {
  const keys = order ? order.filter((k) => map.has(k)) : [...map.keys()];
  return keys.map((k) => ({ key: k, label: labelFn(k), trades: map.get(k), ...stats(map.get(k)) }));
}

// `since` is the start of the period as a timestamp (0 for all time).
// `hide` lists areas (in English, e.g. 'Day of week') to leave out, because the trades were
// filtered down to one side of that comparison.
export function buildInsights({ trades: allTrades, notes, rules, library, accounts = [], since = 0, hide = [] }) {
  const cutoff = since;
  const trades = allTrades
    .filter((t) => new Date(t.date).getTime() >= cutoff)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const overall = stats(trades);
  const findings = [];
  // `sample` is how many trades (or `days`) a finding rests on; small ones are flagged.
  const hidden = hide.map((a) => tn(a));
  const add = (f) => {
    if (hidden.includes(f.area)) return;
    findings.push({ ...f, small: f.days ? f.days < SOLID_DAYS : (f.sample || 0) < SOLID_TRADES });
  };

  // --- Time of day (30-minute windows, the trader's local time) ---
  // Trades from files without times (e.g. Schwab) can't say anything about time of day.
  const byTime = group(trades.filter((t) => !t.timeUnknown), (t) => {
    const d = new Date(t.date);
    return Math.floor((d.getHours() * 60 + d.getMinutes()) / 30) * 30;
  });
  const timeRows = rows(byTime, (k) => `${timeLabel(k)}–${timeLabel(k + 30)}`, [...byTime.keys()].sort((a, b) => a - b));
  const timeSolid = timeRows.filter((r) => r.count >= MIN_TRADES);
  const worstTime = [...timeSolid].sort((a, b) => a.net - b.net)[0];
  const bestTime = [...timeSolid].sort((a, b) => b.net - a.net)[0];
  if (worstTime && worstTime.net < 0) {
    const share = overall.grossLoss ? (byTime.get(worstTime.key).filter((t) => netPnl(t) < 0).reduce((s, t) => s - netPnl(t), 0) / overall.grossLoss) * 100 : 0;
    add({
      kind: 'bad', area: tn('Time of day'), impact: -worstTime.net, sample: worstTime.count, metric: { kind: 'time', key: String(worstTime.key) },
      text: tn(share >= 30
        ? '{time} is your costliest window: {amount} over {trades}, {rate} win rate ({share} of all your losses).'
        : '{time} is your costliest window: {amount} over {trades}, {rate} win rate.',
      { time: worstTime.label, amount: formatMoney(worstTime.net), trades: tradesText(worstTime.count), rate: pct(worstTime.winRate), share: pct(share) }),
      tip: tn('Sit out {time}, or trade it with your smallest size until the numbers improve.', { time: worstTime.label }),
      steps: [
        tn('Without your {time} trades, this period would have been {amount}.', { time: worstTime.label, amount: formatMoney(overall.net - worstTime.net) }),
        tn('Sit out {time} for the next 2 weeks, then compare.', { time: worstTime.label }),
        tn('If you do trade it, use your smallest size: 1 micro contract.'),
        ...(bestTime && bestTime.net > 0 && bestTime.key !== worstTime.key ? [tn('Plan your session around {time}, your best window.', { time: bestTime.label })] : []),
      ],
      rule: tn('No new trades {time}.', { time: worstTime.label }),
    });
  }
  if (bestTime && bestTime.net > 0 && (!worstTime || bestTime.key !== worstTime.key)) {
    add({
      kind: 'good', area: tn('Time of day'), impact: bestTime.net, sample: bestTime.count,
      text: tn('{time} is your best window: {amount} over {trades}, {rate} win rate.',
        { time: bestTime.label, amount: formatMoney(bestTime.net), trades: tradesText(bestTime.count), rate: pct(bestTime.winRate) }),
      tip: tn('Make {time} your main trading window.', { time: bestTime.label }),
    });
  }

  // --- Day of the week ---
  // Weekday of the trading day: a Sunday-evening trade belongs to Monday.
  const byWeekday = group(trades, (t) => new Date(`${tradingDayKey(t.date)}T12:00:00`).getDay());
  const weekdayRows = rows(byWeekday, (k) => tn(WEEKDAYS[k]), [1, 2, 3, 4, 5, 6, 0]);
  const weekdaySolid = weekdayRows.filter((r) => r.count >= MIN_TRADES);
  const worstDay = [...weekdaySolid].sort((a, b) => a.net - b.net)[0];
  const bestDay = [...weekdaySolid].sort((a, b) => b.net - a.net)[0];
  if (worstDay && worstDay.net < 0) {
    add({ kind: 'bad', area: tn('Day of week'), impact: -worstDay.net * 0.8, sample: worstDay.count, metric: { kind: 'weekday', key: String(worstDay.key) },
      text: tn('{days} are your weakest day: {amount} over {trades}.',
        { days: tn(WEEKDAYS_PLURAL[worstDay.key]), amount: formatMoney(worstDay.net), trades: tradesText(worstDay.count) }),
      tip: tn('Trade smaller {onDays}, and review those trades for a pattern.', { onDays: tn(ON_WEEKDAYS[worstDay.key]) }),
      steps: [
        tn('Trade half your normal size {onDays} for the next month.', { onDays: tn(ON_WEEKDAYS[worstDay.key]) }),
        tn('Open your last few {days} in the Trades list and look for what they share: the same setup, time or market.', { days: tn(WEEKDAYS_PLURAL[worstDay.key]) }),
        tn('If nothing stands out, it may be the news calendar. Check for reports on those days before the open.'),
      ],
      rule: tn('Half size {onDays}.', { onDays: tn(ON_WEEKDAYS[worstDay.key]) }) });
  }
  if (bestDay && bestDay.net > 0 && (!worstDay || bestDay.key !== worstDay.key)) {
    add({ kind: 'good', area: tn('Day of week'), impact: bestDay.net * 0.8, sample: bestDay.count,
      text: tn('{days} are your strongest day: {amount} over {trades}.',
        { days: tn(WEEKDAYS_PLURAL[bestDay.key]), amount: formatMoney(bestDay.net), trades: tradesText(bestDay.count) }),
      tip: null });
  }

  // --- After a loss / after a win (next trade the same day) ---
  const afterLoss = [];
  const afterWin = [];
  for (let i = 1; i < trades.length; i++) {
    const prev = trades[i - 1];
    if (tradingDayKey(prev.date) !== tradingDayKey(trades[i].date)) continue;
    if (netPnl(prev) < 0) afterLoss.push(trades[i]);
    else if (netPnl(prev) > 0) afterWin.push(trades[i]);
  }
  const sequence = [
    { key: 'overall', label: tn('Overall'), trades, ...overall },
    { key: 'afterLoss', label: tn('Right after a loss'), trades: afterLoss, ...stats(afterLoss) },
    { key: 'afterWin', label: tn('Right after a win'), trades: afterWin, ...stats(afterWin) },
  ];
  const al = sequence[1];
  const aw = sequence[2];
  if (al.count >= MIN_TRADES && al.winRate < overall.winRate - 10 && al.net < 0) {
    add({ kind: 'bad', area: tn('Tilt'), impact: -al.net, sample: al.count, metric: { kind: 'afterLoss' },
      text: tn('After a losing trade, your next trade wins only {rate} of the time (vs {overall} overall): {amount} over {trades}.',
        { rate: pct(al.winRate), overall: pct(overall.winRate), amount: formatMoney(al.net), trades: tradesText(al.count) }),
      tip: tn('After any loss, take a 15-minute break before your next trade.'),
      steps: [
        tn('Your trades right after a loss lost {amount}. Without them, this period would have been {total}.', { amount: formatMoney(al.net), total: formatMoney(overall.net - al.net) }),
        tn('After any loss, stand up and wait 15 minutes before the next trade. In Live session, tap a reset button to start the timer.'),
        tn('Two losses in a row ends the day.'),
      ],
      rule: tn('After a loss, I wait 15 minutes before my next trade.') });
  } else if (al.count >= MIN_TRADES && al.winRate >= overall.winRate) {
    add({ kind: 'good', area: tn('Tilt'), impact: Math.max(al.net, 1) * 0.5, sample: al.count,
      text: tn('You recover well from losses: trades right after a loss win {rate} of the time.', { rate: pct(al.winRate) }), tip: null });
  }
  if (aw.count >= MIN_TRADES && aw.winRate < overall.winRate - 10 && aw.net < 0) {
    add({ kind: 'bad', area: tn('Overconfidence'), impact: -aw.net, sample: aw.count, metric: { kind: 'afterWin' },
      text: tn('After a winning trade, your next trade wins only {rate} of the time: {amount} over {trades}.',
        { rate: pct(aw.winRate), amount: formatMoney(aw.net), trades: tradesText(aw.count) }),
      tip: tn('After a win, wait for a full A+ setup instead of jumping back in.'),
      steps: [
        tn('Your trades right after a win lost {amount}. A win makes the next trade feel safer than it is.', { amount: formatMoney(aw.net) }),
        tn('After a win, only an A+ setup gets your next trade. Anything less, you wait.'),
        tn('If you hit your daily goal, stop for the day and protect it.'),
      ],
      rule: tn('After a win, only an A+ setup gets my next trade.') });
  }

  // --- Trade number of the day ---
  const nth = new Map();
  const seen = {};
  for (const t of trades) {
    const k = tradingDayKey(t.date);
    seen[k] = (seen[k] || 0) + 1;
    const n = Math.min(seen[k], 4);
    if (!nth.has(n)) nth.set(n, []);
    nth.get(n).push(t);
  }
  const nthRows = rows(nth, (n) => tn(['1st trade', '2nd trade', '3rd trade', '4th trade +'][n - 1]), [1, 2, 3, 4]);
  const late = nthRows.filter((r) => r.key >= 3 && r.count >= MIN_TRADES);
  const lateNet = late.reduce((s, r) => s + r.net, 0);
  const lateCount = late.reduce((s, r) => s + r.count, 0);
  if (late.length && lateNet < 0) {
    add({ kind: 'bad', area: tn('Overtrading'), impact: -lateNet, sample: lateCount, metric: { kind: 'lateTrades' },
      text: tn('Your 3rd and later trades of the day have lost {amount} over {trades}.', { amount: formatMoney(lateNet), trades: tradesText(lateCount) }),
      tip: tn('Set a maximum of 2 trades per day and stop when you hit it.'),
      steps: [
        tn('Without your 3rd and later trades of the day, this period would have been {total}.', { total: formatMoney(overall.net - lateNet) }),
        tn('Set a maximum of 2 trades per day. When you hit it, close the platform.'),
        tn('Write down your 2 trades in Morning prep: the setups you’re waiting for, so the extra ones stand out.'),
      ],
      rule: tn('Maximum 2 trades per day.') });
  }
  const first = nthRows.find((r) => r.key === 1);
  if (first && first.count >= MIN_TRADES && first.net > 0 && first.winRate >= overall.winRate) {
    add({ kind: 'good', area: tn('First trade'), impact: first.net * 0.6, sample: first.count,
      text: tn('Your first trade of the day is your best: {amount} over {trades}, {rate} win rate.', { amount: formatMoney(first.net), trades: tradesText(first.count), rate: pct(first.winRate) }), tip: null });
  }

  // --- Long vs short, symbol, size ---
  const sideRows = rows(group(trades, (t) => t.side || 'Long'), (k) => tn(k), ['Long', 'Short']);
  const symbolRows = rows(group(trades, (t) => t.symbol), (k) => k).sort((a, b) => b.count - a.count).slice(0, 8);
  // Size is compared within one contract only: 1 NQ is about the size of 10 MNQ.
  const sizeKey = (t) => `${rootSymbol(t.symbol)}|${(t.size || 1) > 1 ? 'big' : 'one'}`;
  const sizeMap = group(trades, sizeKey);
  const sizeOrder = [...sizeMap.keys()].sort((a, b) => a.localeCompare(b));
  const sizeRows = rows(sizeMap, (k) => {
    const [symbol, bucket] = k.split('|');
    return tn(bucket === 'one' ? '{symbol}: 1 contract' : '{symbol}: 2+ contracts', { symbol });
  }, sizeOrder);
  const solidSides = sideRows.filter((r) => r.count >= MIN_TRADES);
  if (solidSides.length === 2) {
    const [a, b] = [...solidSides].sort((x, y) => y.net - x.net);
    if (b.net < 0 && a.net > 0) {
      const side = (r) => tn(r.key === 'Short' ? 'short' : 'long');
      add({ kind: 'bad', area: tn('Direction'), impact: -b.net * 0.9, sample: b.count, metric: { kind: 'side', key: b.key },
        text: tn('Your {bad} trades lose money ({badAmount}) while your {good} trades make {goodAmount}.',
          { bad: side(b), badAmount: formatMoney(b.net), good: side(a), goodAmount: formatMoney(a.net) }),
        tip: tn('Be pickier with your {side} trades: only take them on your best setup.', { side: side(b) }),
        steps: [
          tn('Only take {side} trades on your best setup, with the trend on your side.', { side: side(b) }),
          tn('For the next 2 weeks, trade only {good} and see whether your results improve.', { good: side(a) }),
          tn('Review your last {side} trades: were you fighting the trend?', { side: side(b) }),
        ],
        rule: tn('Only take {side} trades on my A+ setup.', { side: side(b) }) });
      add({ kind: 'good', area: tn('Direction'), impact: a.net * 0.5, sample: a.count,
        text: tn('Your {side} trades are working: {amount} over {trades}.', { side: side(a), amount: formatMoney(a.net), trades: tradesText(a.count) }), tip: null });
    }
  }
  const sizeSymbols = [...new Set(sizeRows.map((r) => r.key.split('|')[0]))];
  for (const symbol of sizeSymbols) {
    const big = sizeRows.find((r) => r.key === `${symbol}|big`);
    const one = sizeRows.find((r) => r.key === `${symbol}|one`);
    if (big && one && big.count >= MIN_TRADES && one.count >= MIN_TRADES && big.avg < 0 && big.avg < one.avg) {
      add({ kind: 'bad', area: tn('Position size'), impact: -big.net, sample: big.count + one.count, metric: { kind: 'size', key: symbol },
        text: tn('On {symbol}, trades with 2+ contracts average {big} per trade over {bigTrades}, vs {one} with 1 contract over {oneTrades}.',
          { symbol, big: formatMoney(big.avg), bigTrades: tradesText(big.count), one: formatMoney(one.avg), oneTrades: tradesText(one.count) }),
        tip: tn('Trade 1 contract of {symbol} until your results with bigger size improve.', { symbol }),
        steps: [
          tn('Trade 1 contract of {symbol} until you’ve had 20 trades in a row with a positive average.', { symbol }),
          tn('Add size only on your A+ setup, and only after a green week.'),
          tn('Bigger size changes how you manage a trade. Watch whether you exit winners early with more contracts.'),
        ],
        rule: tn('1 contract of {symbol} until I’m green with it.', { symbol }) });
    }
  }

  // --- Honest focus: which markets and accounts actually pay, and switching after losses ---
  // These go in their own "Honest take" box at the top of Insights, so they're never
  // crowded out of the summary.
  const honest = [];
  // A market that loses while another makes money is a sign to narrow down, not to keep spreading out.
  const marketMap = group(trades, marketOf);
  const marketLabel = (key) => {
    const symbols = [...new Set(marketMap.get(key).map((t) => rootSymbol(t.symbol)))].sort();
    return FAMILY_NAME[key] ? `${tn(FAMILY_NAME[key])} (${symbols.join(', ')})` : key;
  };
  const marketRows = rows(marketMap, marketLabel).filter((r) => r.count >= MIN_TRADES);
  const bestMarket = [...marketRows].sort((a, b) => b.net - a.net)[0];
  // Biggest loser first.
  const losingMarkets = marketRows.filter((r) => r.net < 0 && r !== bestMarket).sort((a, b) => a.net - b.net);
  let marketFinding = null;
  if (bestMarket && bestMarket.net > 0 && losingMarkets.length) {
    const lost = losingMarkets.reduce((sum, r) => sum + r.net, 0);
    const lostTrades = losingMarkets.reduce((sum, r) => sum + r.count, 0);
    const names = listWords(losingMarkets.map((r) => r.label));
    marketFinding = { kind: 'bad', area: tn('Markets'), impact: -lost, sample: lostTrades,
      text: tn('{markets} lost {amount} over {trades}, while {best} made {bestAmount} ({rate} win rate).',
        { markets: names, amount: formatMoney(lost), trades: tradesText(lostTrades), best: bestMarket.label, bestAmount: formatMoney(bestMarket.net), rate: pct(bestMarket.winRate) }),
      tip: tn('Honest take: you trade {best} far better than the rest. Focus on {best} only and pause {markets} until you’re consistently green there.',
        { best: bestMarket.label, markets: names }),
      steps: [
        tn('Without {markets}, this period would have been {total}.', { markets: names, total: formatMoney(overall.net - lost) }),
        tn('Trade only {best} for the next 2 weeks.', { best: bestMarket.label }),
        tn('If you want to keep learning {markets}, paper-trade them until they’re green for 2 weeks.', { markets: names }),
      ],
      rule: tn('I only trade {best} until I’m consistently green in anything else.', { best: bestMarket.label }) };
    add({ kind: 'good', area: tn('Markets'), impact: bestMarket.net * 0.6, sample: bestMarket.count,
      text: tn('{best} is your market: {amount} over {trades}, {rate} win rate.', { best: bestMarket.label, amount: formatMoney(bestMarket.net), trades: tradesText(bestMarket.count), rate: pct(bestMarket.winRate) }), tip: null });
  }
  // The same idea across accounts, when looking at more than one.
  const accountName = (id) => (accounts.find((a) => String(a._id) === String(id)) || {}).name;
  const accountRows = rows(group(trades, (t) => (t.accountId && accountName(t.accountId) ? String(t.accountId) : null)), accountName).filter((r) => r.count >= MIN_TRADES);
  const bestAccount = [...accountRows].sort((a, b) => b.net - a.net)[0];
  const losingAccounts = accountRows.filter((r) => r.net < 0 && r !== bestAccount).sort((a, b) => a.net - b.net);
  if (bestAccount && bestAccount.net > 0 && losingAccounts.length) {
    const lost = losingAccounts.reduce((sum, r) => sum + r.net, 0);
    const names = listWords(losingAccounts.map((r) => r.label));
    if (marketFinding && Math.abs(marketFinding.impact + lost) < 0.01) {
      // Same trades either way (one market per account): one finding, both pieces of advice.
      marketFinding.tip = `${marketFinding.tip} ${tn('That means pausing {accounts} for now.', { accounts: names })}`;
    } else {
      honest.push({ kind: 'bad', area: tn('Accounts'), impact: -lost * 0.95, sample: losingAccounts.reduce((sum, r) => sum + r.count, 0),
        text: tn('{accounts} lost {amount}, while {best} made {bestAmount}.', { accounts: names, amount: formatMoney(lost), best: bestAccount.label, bestAmount: formatMoney(bestAccount.net) }),
        tip: tn('Honest take: pause {accounts} for a couple of weeks and trade only {best}. Fewer accounts means one loss limit and your full attention.',
          { accounts: names, best: bestAccount.label }),
        steps: [
          tn('Pause {accounts} for 2 weeks and trade only {best}.', { accounts: names, best: bestAccount.label }),
          tn('One account means one daily limit to watch, and no temptation to win it back somewhere else.'),
        ],
        rule: tn('I trade only {best} for now.', { best: bestAccount.label }) });
    }
  }
  if (marketFinding) honest.push(marketFinding);
  // Switching after losses: a trade on another account or market after 2+ losses elsewhere that day.
  const switched = [];
  const byDayAll = group(trades, (t) => tradingDayKey(t.date));
  for (const dayTrades of byDayAll.values()) {
    const losses = [];
    for (const t of dayTrades) {
      const place = `${t.accountId || ''}|${marketOf(t)}`;
      if (losses.filter((l) => l !== place).length >= 2) switched.push(t);
      if (netPnl(t) < 0) losses.push(place);
    }
  }
  const switchedNet = switched.reduce((sum, t) => sum + netPnl(t), 0);
  if (switched.length >= 2 && switchedNet < 0) {
    honest.push({ kind: 'bad', area: tn('Switching after losses'), impact: -switchedNet, sample: switched.length,
      text: tn('After 2 or more losses, you moved to another account or market {n} times. Those trades lost {amount}.', { n: switched.length, amount: formatMoney(switchedNet) }),
      tip: tn('Honest take: a new account or market doesn’t reset your day. After 2 losses, stop everywhere, not just on the chart that hurt.'),
      steps: [
        tn('Without those trades, this period would have been {total}.', { total: formatMoney(overall.net - switchedNet) }),
        tn('After 2 losses, close every platform, not just the chart that hurt.'),
        tn('Count your losses across all accounts as one day.'),
      ],
      rule: tn('After 2 losses, I stop on every account and market.') });
  }

  // --- Hold time (trades with an exit time) ---
  const withHold = trades.filter((t) => holdMinutes(t) !== null);
  const holdMap = group(withHold, (t) => HOLD_BUCKETS.findIndex((b) => holdMinutes(t) < b.max));
  const holdRows = rows(holdMap, (i) => tn(HOLD_BUCKETS[i].label), HOLD_BUCKETS.map((_, i) => i));
  const holdSolid = holdRows.filter((r) => r.count >= MIN_TRADES);
  const worstHold = [...holdSolid].sort((a, b) => a.net - b.net)[0];
  const bestHold = [...holdSolid].sort((a, b) => b.net - a.net)[0];
  if (worstHold && worstHold.net < 0) {
    add({ kind: 'bad', area: tn('Hold time'), impact: -worstHold.net * 0.9, sample: worstHold.count, metric: { kind: 'hold', key: String(worstHold.key) },
      text: tn('Trades held {hold} lose money: {amount} over {trades}.',
        { hold: worstHold.label.toLowerCase(), amount: formatMoney(worstHold.net), trades: tradesText(worstHold.count) }),
      tip: tn(worstHold.key <= 1 ? 'Very short trades are often impulsive. Wait for your setup to confirm before entering.' : 'Your longer holds have been losing. Respect your stop and target instead of hoping.'),
      steps: worstHold.key <= 1 ? [
        tn('Wait for a candle to close in your direction before entering. Most very short losers are entries that came too early.'),
        tn('After closing a trade, wait at least 2 minutes before the next one.'),
        tn('Place your stop and target as a bracket order, so you aren’t tempted to jump out at the first wiggle.'),
      ] : [
        tn('Put your stop and target in the platform when you enter, and let one of them close the trade.'),
        tn('If the trade isn’t working after the time your winners usually take, get out at break-even or a small loss.'),
        tn('Never move your stop further away to give a losing trade more room.'),
      ],
      rule: tn(worstHold.key <= 1 ? 'I wait for a candle close before I enter.' : 'My stop and target go in when I enter, and I never move the stop further away.') });
  }
  if (bestHold && bestHold.net > 0 && (!worstHold || bestHold.key !== worstHold.key)) {
    add({ kind: 'good', area: tn('Hold time'), impact: bestHold.net * 0.6, sample: bestHold.count,
      text: tn('Trades held {hold} are your most profitable: {amount} over {trades}.', { hold: bestHold.label.toLowerCase(), amount: formatMoney(bestHold.net), trades: tradesText(bestHold.count) }), tip: null });
  }

  // --- Tags ---
  const tagMap = new Map();
  for (const t of trades) {
    for (const name of t.tags || []) {
      if (!tagMap.has(name)) tagMap.set(name, []);
      tagMap.get(name).push(t);
    }
  }
  const tagRows = rows(tagMap, (k) => k).map((r) => ({ ...r, tagKind: tagKind(r.key, library) })).sort((a, b) => a.net - b.net);
  const worstMistake = tagRows.find((r) => r.tagKind === 'mistake' && r.net < 0 && r.count >= 2);
  const bestSetup = [...tagRows].reverse().find((r) => r.tagKind === 'setup' && r.net > 0 && r.count >= 2);
  if (worstMistake) {
    add({ kind: 'bad', area: tn('Mistakes'), impact: -worstMistake.net, sample: worstMistake.count, metric: { kind: 'tag', key: worstMistake.key },
      text: tn('"{tag}" trades cost you {amount} over {trades}.', { tag: worstMistake.label, amount: formatMoney(worstMistake.net), trades: tradesText(worstMistake.count) }),
      tip: tn('Add a rule to your Morning prep that stops "{tag}" before it happens.', { tag: worstMistake.label }),
      steps: [
        tn('Without your "{tag}" trades, this period would have been {total}.', { tag: worstMistake.label, total: formatMoney(overall.net - worstMistake.net) }),
        tn('Before every entry, ask yourself: is this a "{tag}"? If yes, skip it.', { tag: worstMistake.label }),
        tn('Open your "{tag}" trades and write down what happened just before each one. That’s your trigger to watch for.', { tag: worstMistake.label }),
      ],
      rule: tn('No "{tag}" trades. If I spot one, I skip it.', { tag: worstMistake.label }) });
  }
  if (bestSetup) {
    add({ kind: 'good', area: tn('Setups'), impact: bestSetup.net, sample: bestSetup.count,
      text: tn('Your "{tag}" setup is working: {amount} over {trades}, {rate} win rate.',
        { tag: bestSetup.label, amount: formatMoney(bestSetup.net), trades: tradesText(bestSetup.count), rate: pct(bestSetup.winRate) }),
      tip: tn('Look for more "{tag}" setups and fewer of the rest.', { tag: bestSetup.label }) });
  }

  // --- Routine vs results (per trading day, from the journal) ---
  const dayNet = {};
  for (const t of trades) dayNet[tradingDayKey(t.date)] = (dayNet[tradingDayKey(t.date)] || 0) + netPnl(t);
  const days = Object.keys(dayNet);
  // goodPhrase finishes the sentence "…vs $X on days when ___".
  const compare = (id, label, test, goodLabel, badLabel, goodPhrase) => {
    const yes = days.filter((d) => test(notes[d]) === true);
    const no = days.filter((d) => test(notes[d]) === false);
    if (yes.length < MIN_DAYS || no.length < MIN_DAYS) return null;
    const avg = (list) => list.reduce((s, d) => s + dayNet[d], 0) / list.length;
    return { id, label: tn(label), goodPhrase: tn(goodPhrase), good: { label: tn(goodLabel), days: yes.length, avg: avg(yes) }, bad: { label: tn(badLabel), days: no.length, avg: avg(no) } };
  };
  const routine = [
    rules.length ? compare('rules', 'Following your rules', (n) => (n && n.rulesFollowed && n.rulesFollowed.length ? rules.every((r) => n.rulesFollowed.includes(r)) : undefined),
      'Followed all rules', 'Broke a rule', 'you followed all your rules') : null,
    compare('sleep', 'Sleep', (n) => (n && n.sleepHours != null ? n.sleepHours >= 6 : undefined), '6+ hours of sleep', 'Under 6 hours',
      'you slept 6 hours or more'),
    compare('emotions', 'Emotions', (n) => (n && n.emotionImpact ? !(n.emotionsHit && n.emotionsHit.length) && n.emotionImpact === 'none' : undefined),
      'Emotions in check', 'FOMO, tilt or other emotions hit', 'your emotions were in check'),
    compare('score', 'Day score', (n) => (n && n.score ? n.score >= 7 : undefined), 'Scored 7+', 'Scored 6 or less', 'you scored your day 7 or higher'),
    compare('plan', 'Morning plan', (n) => (n ? Boolean(n.plan && n.plan.trim()) : false), 'Wrote a plan', 'No plan', 'you wrote a plan'),
    compare('focus', 'Daily focus', (n) => (n && n.focusFollowed ? n.focusFollowed === 'yes' : undefined), 'Followed your focus', 'Didn’t fully follow it',
      'you followed your daily focus'),
  ].filter(Boolean);
  for (const r of routine) {
    const gap = r.good.avg - r.bad.avg;
    if (gap > 0 && r.bad.avg < 0) {
      add({ kind: 'bad', area: r.label, impact: gap * r.bad.days, days: Math.min(r.bad.days, r.good.days), metric: { kind: 'overall' },
        text: tn('{label}: {amount} per day on average over {days}, vs {other} over {otherDays} when {phrase}.',
          { label: r.bad.label, amount: formatMoney(r.bad.avg), days: daysText(r.bad.days), other: formatMoney(r.good.avg), otherDays: daysText(r.good.days), phrase: r.goodPhrase }),
        tip: tn(r.id === 'sleep' ? 'On short-sleep days, cut your size in half or sit out.'
          : r.id === 'plan' ? 'Write your plan in Morning prep before every session.'
          : r.id === 'emotions' ? "When your check-in shows stress or yesterday's slip-ups, trade smaller."
          : 'Your process drives your results. Make your rules non-negotiable.'),
        steps: [
          tn('On those days you average {gap} less than when {phrase}: about {total} over {days}.', { gap: usd.format(gap), phrase: r.goodPhrase, days: daysText(r.bad.days), total: usd.format(gap * r.bad.days) }),
          tn(r.id === 'sleep' ? 'On days you slept under 6 hours, trade half size or sit out. Your Morning check-in tells you which days those are.'
            : r.id === 'plan' ? 'Write your plan in Morning prep before every session: your bias, levels, setups and max loss.'
            : r.id === 'emotions' ? 'When your check-in shows stress or a strong emotion, trade half size until it passes.'
            : r.id === 'score' ? 'Read your reflection from your best days before the open. Copy what you did on them.'
            : r.id === 'focus' ? 'Read your daily focus at the start of every session and keep it on screen.'
            : 'Read your rules before the open, and end the day the first time you break one.'),
        ],
        rule: tn(r.id === 'sleep' ? 'Under 6 hours of sleep: half size or no trading.'
          : r.id === 'plan' ? 'No plan written, no trading.'
          : r.id === 'emotions' ? 'Stressed or emotional: half size.'
          : r.id === 'focus' ? 'I read my daily focus before my first trade.'
          : r.id === 'score' ? 'I trade the way I did on my best days.'
          : 'The first time I break a rule, I stop for the day.') });
    } else if (gap > 0 && r.good.avg > 0) {
      add({ kind: 'good', area: r.label, impact: gap * r.good.days * 0.5, days: Math.min(r.bad.days, r.good.days),
        text: tn('{label}: {amount} per day on average over {days}, vs {other} over {otherDays} otherwise.',
          { label: r.good.label, amount: formatMoney(r.good.avg), days: daysText(r.good.days), other: formatMoney(r.bad.avg), otherDays: daysText(r.bad.days) }), tip: null });
    }
  }

  // --- Reset button taps (Live session): trades within 15 minutes after a tap ---
  const taps = [];
  for (const [key, n] of Object.entries(notes || {})) {
    for (const e of (n && n.sessionLog) || []) {
      if (e.feeling || (e.type === 'feeling' && RESET_TEXT.test(e.text || ''))) taps.push({ key, time: new Date(e.time).getTime() });
    }
  }
  let resetRows = null;
  if (taps.length) {
    const afterTap = (t) => {
      const at = new Date(t.date).getTime();
      return taps.some((tap) => at >= tap.time && at - tap.time <= 15 * 60000);
    };
    const near = trades.filter(afterTap);
    const rest = trades.filter((t) => !afterTap(t));
    resetRows = [
      { key: 'afterTap', label: tn('Within 15 min after a tap'), trades: near, ...stats(near) },
      { key: 'otherTrades', label: tn('All other trades'), trades: rest, ...stats(rest) },
    ];
    const r = resetRows[0];
    if (r.count >= MIN_TRADES && r.net < 0 && r.winRate < overall.winRate - 10) {
      add({ kind: 'bad', area: tn('Reset button'), impact: -r.net, sample: r.count, metric: { kind: 'reset' },
        text: tn('Trades within 15 minutes after you tapped a reset button lost {amount} over {trades}, {rate} win rate.',
          { amount: formatMoney(r.net), trades: tradesText(r.count), rate: pct(r.winRate) }),
        tip: tn('When you tap a reset button, take the full timeout before your next trade.'),
        steps: [
          tn('A tap means you already felt it coming. Take the full timeout before the next trade, every time.'),
          tn('If you tap twice in a day, end the day.'),
        ],
        rule: tn('When I tap a reset button, I take the full timeout.') });
    }
  }

  // Different angles on the very same trades (e.g. "9 AM", "shorts" and "under 2
  // minutes" all being the same 20 trades) produce the same dollar figure; keep
  // only the strongest of those so the summary shows distinct points.
  const byImpact = (a, b) => b.impact - a.impact;
  const distinct = (list) => {
    const seenAmounts = new Set();
    return list.filter((f) => {
      const amount = (f.text.match(/[+-]\$[\d,]+\.\d\d/) || [f.text])[0];
      if (seenAmounts.has(amount)) return false;
      seenAmounts.add(amount);
      return true;
    });
  };
  const bad = distinct(findings.filter((f) => f.kind === 'bad').sort(byImpact));
  const good = distinct(findings.filter((f) => f.kind === 'good').sort(byImpact));

  return {
    overall,
    tradeCount: trades.length,
    honest: honest.filter((f) => !hidden.includes(f.area)).map((f) => ({ ...f, small: f.sample < SOLID_TRADES })).sort((a, b) => b.impact - a.impact),
    working: good.slice(0, 4),
    costing: bad.slice(0, 4),
    tryNext: bad.filter((f) => f.tip).slice(0, 3).map((f) => ({ area: f.area, tip: f.tip, metric: f.metric, finding: f.text })),
    timeRows,
    weekdayRows,
    sequence,
    nthRows,
    sideRows,
    symbolRows,
    sizeRows,
    holdRows,
    holdCoverage: trades.length ? withHold.length / trades.length : 0,
    tagRows,
    routine,
    resetRows,
  };
}
